import { request } from 'api/request';
import { JourneyType, TravellerJourneyListItem } from 'api/types';

import {
  BulkCreateTraveller,
  JourneyTravellersStatsType,
  ModifiedTravellerJourney,
  TravellerJourneyActions,
  TravellerJourneyDataResult,
  TravellerJourneyDetails,
  TravellerJourneyIssues,
  TravellerJourneySource,
  TravellerJourneyStatusType,
} from './types';

export const loadJourneyTravellers = async (shortId: string, queryString?: string) => {
  const search = queryString ? `${queryString}&journey=${shortId}` : `?journey=${shortId}`;
  const response = await request.get<TravellerJourneyDataResult>(`v1/traveller-journeys/${search}`);
  return response.data;
};

export const loadJourneyTravellersV2 = async (shortId: JourneyType['shortId']) => {
  const response = await request.get<TravellerJourneyListItem[]>(
    `v2/traveller-journeys/?journey=${shortId}`
  );
  return response.data;
};

export const loadJourneyTravellersStatistics = async (shortId: string, queryString?: string) => {
  const search = queryString ? `${queryString}&journey=${shortId}` : `?journey=${shortId}`;
  const response = await request.get<JourneyTravellersStatsType>(
    `v1/traveller-journeys/statistics/${search}`
  );
  return response.data;
};

export const loadJourneyTravellerListActions = async (travellerJourneyIds: Array<string>) => {
  const response = await request.get<TravellerJourneyActions>(
    `v1/traveller-journeys/actions/?traveller_journeys=${travellerJourneyIds.join(',')}`
  );
  return response.data;
};

export const getJourneyTraveller = async (id: string) => {
  const response = await request.get<TravellerJourneyDetails>(`v1/traveller-journeys/${id}/`);
  return response.data;
};

export const getJourneyTravellerIssues = async (travellerId: string) => {
  const response = await request.get<TravellerJourneyIssues>(
    `v1/traveller-journeys/${travellerId}/issues/`
  );
  return response.data;
};

export const getTravellerJourneysStatus = async (ids: string[]) => {
  const response = await request.get<TravellerJourneyStatusType[]>(
    `v1/traveller-journeys/status/?ids=${ids}`
  );
  return response.data;
};

export const getTravellerJourneySource = async (shortId: string) => {
  const response = await request.get<TravellerJourneySource>(
    `v1/traveller-journeys/${shortId}/source/`
  );
  return response.data;
};

export const startJourneyTraveller = async (id: string) => {
  const response = await request.post(`v1/traveller-journeys/${id}/start/`);
  return response.data;
};

export const pauseJourneyTraveller = async (id: string) => {
  const response = await request.post(`v1/traveller-journeys/${id}/pause/`);
  return response.data;
};

export const resumeJourneyTraveller = async (id: string) => {
  const response = await request.post(`v1/traveller-journeys/${id}/resume/`);
  return response.data;
};

export const resetJourneyTraveller = async (id: string) => {
  const response = await request.post(`v1/traveller-journeys/${id}/reset/`);
  return response.data;
};

export const createJourneyTraveller = async (data: FormData) => {
  const response = await request.post(`v1/traveller-journeys/`, data);
  return response.data;
};

export const editJourneyTraveller = async (id: string, data: FormData) => {
  const response = await request.patch(`v1/traveller-journeys/${id}/`, data);
  return response.data;
};

export const deleteJourneyTraveller = async (id: string) => {
  const response = await request.delete(`v1/traveller-journeys/${id}/`);
  return response.data;
};

export const bulkCreateJourneyTraveller = async (
  id: string,
  data: { travellers: BulkCreateTraveller[]; isSave: boolean; updateExisting?: boolean }
) => {
  const response = await request.post(`v1/traveller-journeys/${id}/bulk-create/`, data);
  return response.data;
};

export async function getBulkCreateTemplate(journeyId: string) {
  const response = await request.get(`v1/traveller-journeys/${journeyId}/bulk-create-template/`, {
    responseType: 'blob',
  });
  return response.data;
}

export const loadModifiedTravellerJourneys = async (id: string) => {
  const response = await request.get<ModifiedTravellerJourney[]>(
    `v1/traveller-journeys/modified/?journey_id=${id}`
  );
  return response.data;
};

export const changeModifiedTravellerJourneys = async (data: string[]) => {
  const response = await request.post('v1/traveller-journeys/modified/', data);
  return response.data;
};

export const bulkStartJourneyTraveller = async ({
  journeyId,
  data,
  queryString,
}: {
  journeyId: string;
  data: { ids: string[] };
  queryString?: string;
}) => {
  const search = queryString ? `${queryString}&journey=${journeyId}` : `?journey=${journeyId}`;
  const response = await request.post(`v1/traveller-journeys/bulk-start/${search}`, data);
  return response.data;
};

export const bulkDeleteJourneyTraveller = async ({
  journeyId,
  data,
  queryString,
}: {
  journeyId: string;
  data: { ids: string[] };
  queryString?: string;
}) => {
  const search = queryString ? `${queryString}&journey=${journeyId}` : `?journey=${journeyId}`;
  const response = await request.post(`v1/traveller-journeys/bulk-delete/${search}`, data);
  return response.data;
};

export const bulkPauseJourneyTraveller = async ({
  journeyId,
  data,
  queryString,
}: {
  journeyId: string;
  data: { ids: string[] };
  queryString?: string;
}) => {
  const search = queryString ? `${queryString}&journey=${journeyId}` : `?journey=${journeyId}`;
  const response = await request.post(`v1/traveller-journeys/bulk-pause/${search}`, data);
  return response.data;
};

export const bulkResumeJourneyTraveller = async ({
  journeyId,
  data,
  queryString,
}: {
  journeyId: string;
  data: { ids: string[] };
  queryString?: string;
}) => {
  const search = queryString ? `${queryString}&journey=${journeyId}` : `?journey=${journeyId}`;
  const response = await request.post(`v1/traveller-journeys/bulk-resume/${search}`, data);
  return response.data;
};

export const bulkResetJourneyTraveller = async ({
  journeyId,
  data,
  queryString,
}: {
  journeyId: string;
  data: { ids: string[] };
  queryString?: string;
}) => {
  const search = queryString ? `${queryString}&journey=${journeyId}` : `?journey=${journeyId}`;
  const response = await request.post(`v1/traveller-journeys/bulk-reset/${search}`, data);
  return response.data;
};
